/**
 * The progress frame the backend emits over SSE (`GET /api/scans/stream`) and
 * echoes from the polling lane (`GET /api/scan-status/:id`), plus the single
 * monotonic clamp both apps display it through.
 *
 * This used to exist twice under two names — `mergeProgressFrame` in the
 * dashboard's api.ts and `applyProgress` inside the marketing widget — with
 * different semantics for an out-of-order frame (issue #740).
 */

import type { ProgressStage, ScanQueueStatus } from '@privisy/api-types';

export interface ScanProgress {
  /**
   * Typed against the shared wire contract (@privisy/api-types, issue #779)
   * so a typo'd stage literal anywhere this type is constructed is a compile
   * error. This is strictly the wire type — a caller that needs a
   * client-only synthetic stage (e.g. the marketing widget's local
   * 'starting' frame, seeded before the stream connects) defines its own
   * widened local type rather than loosening this one.
   */
  stage: ProgressStage;
  message: string;
  percentage: number;
  /** Only on a `'queued'` frame: scans ahead of this one and a rough wait. */
  queue?: ScanQueueStatus;
  /**
   * Terminal frames carry the scan payload. Typed `unknown` here because the
   * two apps model a scan result differently; each narrows it at its own edge.
   */
  result?: unknown;
  error?: string;
}

/**
 * Merge an incoming progress frame with the previously displayed one, keeping
 * the displayed stage/percentage monotonically non-decreasing.
 *
 * Stage 4 (GPC validation) and Stage 5 (privacy policy analysis) run
 * concurrently in the backend (packages/backend/src/scanner/scanner.ts), so
 * their frames can legitimately arrive out of numeric order — either can
 * finish first. Pinning only `percentage` while keeping the incoming `stage`
 * would still let the stage label (and the rail step it drives in the
 * dashboard's ScanProgress.tsx) jump backward, so an out-of-order frame is
 * dropped entirely rather than merged.
 *
 * Equal percentages take the incoming frame: that is how a message-only
 * update ("Still scanning, please wait...") reaches the UI.
 */
export function mergeProgressFrame<T extends { percentage: number }>(
  prev: T | null,
  next: T,
): T {
  if (prev && prev.percentage > next.percentage) return prev;
  return next;
}
